import { PayableRow, PayableStatus } from '@bariendo/lib/payables';
import { Box, Checkbox, SegmentedControl, UnstyledButton } from '@mantine/core';
import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'mantine-react-table';
import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { HubTable } from '@/components/Hub/HubTable';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { NotePopoverCell } from '@/components/Hub/NotePopoverCell';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { useNow } from '@/hooks/useNow';
import { CohortTruncationNotice } from '@/pages/PatientListPage/components/CohortTruncationNotice';
import { usePatientListFilters } from '@/pages/PatientListPage/hooks/usePatientListFilters';
import { alert } from '@/services/alert';
import { downloadAttachmentBlob } from '@/services/attachments';
import { ORBIT_DISABLED, ORBIT_NEEDS_ACTION } from '@/theme/colors';
import { LedgerCell, LedgerEmptyCell } from '../ledger/LedgerCell';
import { formatLedgerMoney, ledgerDatePresets } from '../ledger/ledgerRows';
import { ActualCell } from './ActualCell';
import { costProcedureLabel } from './costSchedule';
import { CostScheduleView } from './CostScheduleView';
import { MarkPaidBar } from './MarkPaidBar';
import {
  DEFAULT_PAYABLE_FILTERS,
  feeTypeLabel,
  filterPayables,
  formatPayableDate,
  isClawback,
  isFiltered,
  isPayable,
  PayableFiltersState,
  payablesCsv,
  payablesCsvFilename,
  payeeOptions,
  quarterMonths,
  sumActual,
} from './payableRows';
import { PayablesByMonth } from './PayablesByMonth';
import { PayableActions, usePayableActions } from './usePayableActions';
import { usePayables } from './usePayables';

/** Finance ops → Payables (BAR-3046), as the prototype draws it. */

const VIEW_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

const BUTTON_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: '#fff',
  color: 'black',
  whiteSpace: 'nowrap',
};

const MONEY_STYLE: CSSProperties = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
const RIGHT = { align: 'right' as const };
const DEFAULT_PRESET = 'this-month';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'facility', label: 'Facility' },
  { value: 'anesthesia', label: 'Anesthesia' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'pn', label: 'PN commission and clawback' },
];
const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'all', label: 'All' },
];

type PayablesView = 'lines' | 'month' | 'cost-schedule';
const VIEW_OPTIONS: { label: string; value: PayablesView }[] = [
  { label: 'Lines', value: 'lines' },
  { label: 'By month', value: 'month' },
  { label: 'Cost schedule', value: 'cost-schedule' },
];
const viewOf = (value: string | null): PayablesView =>
  VIEW_OPTIONS.find((option) => option.value === value)?.value ?? 'lines';

const textCell = (value: string | null | undefined): ReactElement =>
  value ? <LedgerCell title={value}>{value}</LedgerCell> : <LedgerEmptyCell />;

/**
 * Selected line ids, each with the status it had when picked. A line counts as selected only while
 * it still has that status, so a line a timed-out Mark paid moved to Paid never comes back selected
 * (BAR-3125), and a reload that changes nothing keeps the selection.
 */
type PayableSelection = Map<string, PayableStatus>;

const isSelected = (selection: PayableSelection, row: PayableRow): boolean => selection.get(row.id) === row.status;

function payableColumns(
  actions: PayableActions,
  selected: PayableSelection,
  toggle: (row: PayableRow, on: boolean) => void
): MRT_ColumnDef<PayableRow>[] {
  return [
    {
      id: 'select',
      header: '',
      size: 28,
      enableSorting: false,
      // A Paid line is selectable too, to mark it unpaid (BAR-3125).
      Cell: ({ row }) =>
        row.original?.status === 'UNPAID' || row.original?.status === 'PAID' ? (
          <Checkbox
            size="xs"
            aria-label="Select line"
            checked={isSelected(selected, row.original)}
            disabled={row.original.status === 'UNPAID' && !isPayable(row.original)}
            onChange={(event) => toggle(row.original, event.currentTarget.checked)}
          />
        ) : null,
    },
    {
      id: 'patient',
      header: 'Patient',
      size: 140,
      grow: true,
      enableSorting: false,
      Cell: ({ row }) => textCell(row.original?.patientName),
    },
    {
      id: 'procedure',
      header: 'Procedure',
      size: 104,
      enableSorting: false,
      Cell: ({ row }) => textCell(row.original?.procedure ? costProcedureLabel(row.original.procedure) : null),
    },
    {
      id: 'payee',
      header: 'Payee',
      size: 156,
      enableSorting: false,
      Cell: ({ row }) => textCell(row.original?.payeeName),
    },
    {
      id: 'feeType',
      header: 'Fee type',
      size: 116,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original?.feeType ? (
          <LedgerCell color={isClawback(row.original) ? ORBIT_NEEDS_ACTION : undefined}>
            {feeTypeLabel(row.original)}
          </LedgerCell>
        ) : null,
    },
    {
      id: 'date',
      header: 'Date',
      size: 96,
      enableSorting: false,
      Cell: ({ row }) => textCell(formatPayableDate(row.original?.serviceDate ?? null)),
    },
    {
      id: 'scheduled',
      header: 'Scheduled',
      size: 96,
      enableSorting: false,
      mantineTableHeadCellProps: RIGHT,
      mantineTableBodyCellProps: RIGHT,
      Cell: ({ row }) =>
        row.original?.scheduledAmount === null || row.original?.scheduledAmount === undefined ? (
          <LedgerCell color={ORBIT_DISABLED} style={MONEY_STYLE}>
            —
          </LedgerCell>
        ) : (
          <LedgerCell style={MONEY_STYLE}>{formatLedgerMoney(row.original.scheduledAmount)}</LedgerCell>
        ),
    },
    {
      id: 'actual',
      header: 'Actual',
      size: 104,
      enableSorting: false,
      mantineTableHeadCellProps: RIGHT,
      mantineTableBodyCellProps: RIGHT,
      Cell: ({ row }) => (row.original?.id ? <ActualCell row={row.original} actions={actions} /> : null),
    },
    {
      id: 'paidOn',
      header: 'Paid on',
      size: 96,
      enableSorting: false,
      Cell: ({ row }) => textCell(formatPayableDate(row.original?.paidOn ?? null)),
    },
    {
      id: 'reference',
      header: 'Reference',
      size: 98,
      enableSorting: false,
      Cell: ({ row }) => textCell(row.original?.reference),
    },
    {
      id: 'note',
      header: '',
      size: 28,
      enableSorting: false,
      Cell: ({ row }) => {
        const line = row.original;
        return line?.id ? <NotePopoverCell note={line.note} onSave={(note) => actions.saveNote(line, note)} /> : null;
      },
    },
  ];
}

export interface PayablesTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

/** Payables: the lines to check, or By month to write the payments (BAR-3046); the view lives in `?view=`. */
export function PayablesTab({ now }: PayablesTabProps): ReactElement {
  const resolvedNow = useNow(now);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = viewOf(searchParams.get('view'));
  const [filters, setFilters] = useState<PayableFiltersState>(DEFAULT_PAYABLE_FILTERS);
  const dateFilter = usePatientListFilters<PayableRow>({
    resetKey: 'finance-payables',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });
  const months = quarterMonths(resolvedNow);
  const quarter = useMemo(
    () => ({
      from: `${months[0]}-01`,
      to: dayjs(`${months[2]}-01`).endOf('month').format('YYYY-MM-DD'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the quarter, not the clock's every tick
    [months[0]]
  );
  const payables = usePayables(view === 'month' ? quarter : dateFilter.range);

  useEffect(() => {
    if (payables.error) alert.error(`Couldn't load payables: ${payables.error}`);
  }, [payables.error]);

  const presets = useMemo(() => ledgerDatePresets(resolvedNow), [resolvedNow]);
  const payees = useMemo(
    () => [{ value: 'all', label: 'All' }, ...payeeOptions(payables.payables, filters.type)],
    [payables.payables, filters.type]
  );
  const set =
    <K extends keyof PayableFiltersState>(key: K) =>
    (value: string) =>
      setFilters((current) => ({
        ...current,
        [key]: value as PayableFiltersState[K],
        ...(key === 'type' ? { payee: 'all' } : {}),
      }));

  return (
    <Box style={VIEW_STYLE}>
      <Box style={{ padding: '12px 16px 0' }}>
        <SegmentedControl
          size="xs"
          data={VIEW_OPTIONS}
          value={view}
          onChange={(value) =>
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('view', value);
              return next;
            })
          }
        />
      </Box>
      <Box mt={12} style={VIEW_STYLE}>
        {view === 'lines' ? (
          <PayableLines
            payables={payables}
            filters={filters}
            setFilters={setFilters}
            set={set}
            payees={payees}
            date={
              <ListRangeControl
                label="Month"
                presets={presets}
                presetId={dateFilter.presetId}
                value={dateFilter.range}
                onChange={dateFilter.setRange}
              />
            }
            now={resolvedNow}
          />
        ) : view === 'cost-schedule' ? (
          <CostScheduleView now={resolvedNow} />
        ) : (
          <Box style={VIEW_STYLE}>
            <QueueFilterBar
              filters={[
                { label: 'Fee type', value: filters.type, options: TYPE_OPTIONS, onChange: set('type') },
                { label: 'Payee', value: filters.payee, options: payees, onChange: set('payee') },
              ]}
              isFiltered={filters.type !== 'all' || filters.payee !== 'all'}
              onReset={() => setFilters(DEFAULT_PAYABLE_FILTERS)}
            />
            <PayablesByMonth payables={payables} filters={filters} now={resolvedNow} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PayableLines({
  payables,
  filters,
  setFilters,
  set,
  payees,
  date,
  now,
}: {
  payables: ReturnType<typeof usePayables>;
  filters: PayableFiltersState;
  setFilters: (filters: PayableFiltersState) => void;
  set: <K extends keyof PayableFiltersState>(key: K) => (value: string) => void;
  payees: { value: string; label: string }[];
  date: ReactElement;
  now: Date;
}): ReactElement {
  const actions = usePayableActions(payables.reload);
  const [selected, setSelected] = useState<PayableSelection>(new Map());
  // Drop picks whose line changed status or left the range, so they can't return if it changes back.
  useEffect(() => {
    const current = new Map(payables.payables.map((row) => [row.id, row.status]));
    setSelected((selection) => {
      const kept = new Map([...selection].filter(([id, status]) => current.get(id) === status));
      return kept.size === selection.size ? selection : kept;
    });
  }, [payables.payables]);
  const visible = useMemo(() => filterPayables(payables.payables, filters), [payables.payables, filters]);
  const selectedRows = useMemo(
    () => visible.filter((row) => isSelected(selected, row) && isPayable(row)),
    [visible, selected]
  );
  const selectedPaid = useMemo(
    () => visible.filter((row) => isSelected(selected, row) && row.status === 'PAID'),
    [visible, selected]
  );
  const columns = useMemo(
    () =>
      payableColumns(actions, selected, (row, on) =>
        setSelected((current) => {
          const next = new Map(current);
          if (on) next.set(row.id, row.status);
          else next.delete(row.id);
          return next;
        })
      ),
    [actions, selected]
  );
  const open = visible.filter((row) => row.status === 'UNPAID');

  const exportCsv = (): void => {
    const url = URL.createObjectURL(new Blob([payablesCsv(visible)], { type: 'text/csv' }));
    downloadAttachmentBlob(url, payablesCsvFilename(now));
  };

  return (
    <Box style={VIEW_STYLE}>
      <QueueFilterBar
        date={date}
        filters={[
          { label: 'Fee type', value: filters.type, options: TYPE_OPTIONS, onChange: set('type') },
          { label: 'Payee', value: filters.payee, options: payees, onChange: set('payee') },
          { label: 'Status', value: filters.status, options: STATUS_OPTIONS, onChange: set('status') },
        ]}
        actions={
          <UnstyledButton style={BUTTON_STYLE} onClick={exportCsv}>
            Export CSV
          </UnstyledButton>
        }
        isFiltered={isFiltered(filters)}
        onReset={() => setFilters(DEFAULT_PAYABLE_FILTERS)}
      />
      {payables.hasMore && <CohortTruncationNotice loaded={payables.payables.length} />}
      <MarkPaidBar
        selected={selectedRows}
        paid={selectedPaid}
        actions={actions}
        onClear={() => setSelected(new Map())}
        now={now}
      />
      <HubTable
        columns={columns}
        data={visible}
        loading={payables.loading}
        emptyText="No lines"
        emptyFallback={
          payables.error && <LoadFailed what="payables" error={payables.error} onRetry={payables.reload} />
        }
      />
      {!payables.error && (
        <QueueFooter
          shown={visible.length}
          summary={
            <>
              <b>{visible.length}</b> lines · actual <b>{formatLedgerMoney(sumActual(visible))}</b>
              {open.length > 0 && (
                <>
                  {' '}
                  · open <b>{formatLedgerMoney(sumActual(open))}</b>
                </>
              )}
            </>
          }
        />
      )}
    </Box>
  );
}
